import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { ClickAction, useOnClickAction } from 'hook/useOnClickAction'
import { useRouter } from 'next/router'
import { MouseEvent, ReactNode } from 'react'
import { getPaddingStyle, Padding, styles } from 'util/style'
import css from './Link.module.scss'

interface Props {
  action?: ClickAction
  children?: ReactNode
  className?: string
  disabled?: boolean
  href?: string
  pad?: Padding
  path?: string
  primary?: boolean
  secondary?: boolean
  small?: boolean
  text?: boolean
  wide?: boolean
  wrap?: boolean
  hover?: boolean
  here?: boolean
}

export const Link = (all: Props) => {
  const router = useRouter()
  const {
    children,
    href,
    path,
    primary,
    secondary,
    small,
    text,
    wide,
    wrap,
    hover,
    here,
    ...props
  } = all
  const { busy, onClick } = useOnClickAction(props)

  const pad = getPaddingStyle(props.pad)
  const style = styles(pad)

  const link = href ?? path ?? undefined
  const target = href && !here ? '_blank' : undefined
  const rel = href ? 'noopener' : undefined
  const disabled = props.disabled || busy || (!link && !onClick)

  const className = clsx(
    props.className,
    css.link,
    disabled && css.disabled,
    secondary && css.secondary,
    primary && css.primary,
    text && css.text,
    small && css.small,
    wide && css.wide,
    wrap && css.wrap,
    hover === false && css.withoutHover,
  )

  const onClickInner = useLatestCallback((event: MouseEvent) => {
    if (disabled) return
    if (onClick) onClick(event)
    if (!link) event.preventDefault()

    const { metaKey, ctrlKey, altKey, shiftKey } = event
    const meta = Boolean(metaKey || ctrlKey || altKey || shiftKey)
    if (meta) return

    if (path && !event.isDefaultPrevented()) {
      event.preventDefault()
      void router.push(path)
    }
  })

  return (
    <a
      className={className}
      style={style}
      href={link}
      target={target}
      rel={rel}
      onClick={onClickInner}
    >
      {children}
    </a>
  )
}
